<template>
	<div class="pronto-container">
		<Login v-if="showLogin" />
		<OAuth v-if="!showLogin && showOAuth" />
		<Meeting v-if="!showLogin && showMeeting" />
		<Home v-if="shouldRenderHome" />
		<Browser v-if="isMS && !store.config.embedded && !isWrapperManager" />
		<Omnibox v-if="omnibox" @close="closeOmnibox" />
		<div
			v-if="!store.config.embedded && !isWrapperManager"
			class="accessibility-highlight-border"
		></div>
		<StorageError v-if="showError" />
		<AuthLoader
			v-if="store.config.wrapper && !store.auth.loggedin && !isMS && !showError"
		/>
		<WrapperManager
			v-if="
				store.auth.loggedin &&
				!store.termsPrompt &&
				!isMS &&
				!showError &&
				isWrapperManager
			"
		/>
		<AcceptTerms v-if="showAcceptTerms" />
		<RouterView v-if="shouldRenderRouterView" />

		<DevTools v-if="showDevTools" />
		<GlobalAlerts />
		<DesktopAppUpdateAlert v-if="isDesktopApp" />
		<AlertModal v-if="alertModalProps" v-bind="alertModalProps" />
		<UserModal
			v-if="userModalUserId"
			:userId="userModalUserId"
			:chatId="userModalChatId"
			@close="userModalUserId = null"
		/>
	</div>
</template>
<script type="text/javascript">
import { defineAsyncComponent } from 'vue'
import store, { getOrganization } from './store'
import Home from './components/Home'
import OAuth from './components/OAuth'
import Browser from './components/Browser'
import Omnibox from './components/Omnibox'
import moment from 'moment-timezone'
import StorageError from './components/StorageError'
import AuthLoader from './components/AuthLoader'
import WrapperManager from './components/WrapperManager'
import AcceptTerms from './components/AcceptTerms'
import Login from './components/Login'
import Meeting from './components/Meeting'
import { browser } from '@/store'
import UserModal from '@/components/common/UserModal'
import { GlobalAlerts, AlertModal } from '@/components/common'
import { highlightFocus, logoutUser, getLoginUrl } from './helperMethods'
import { listenForKeyboardShortcut } from './helpers/keyboard'
import { IMAGE_MAX_WIDTH } from '@/helpers/constants'
import { storage } from '@/helpers/utils'
import { getPusherInstance } from '@/helpers/pusher'
import DesktopAppUpdateAlert from '@/components/desktop/DesktopAppUpdateAlert'

const DevTools = defineAsyncComponent({
	loader: () => {
		const enabled = storage.get('pronto_dev_tools') || false
		return enabled ? import('@/components/DevTools') : Promise.resolve()
	},
	delay: 3000,
	timeout: 10000,
})

export default {
	components: {
		// Login: () => import('./components/Login'),
		Login,
		Home,
		OAuth,
		Browser,
		Omnibox,
		StorageError,
		AuthLoader,
		WrapperManager,
		AcceptTerms,
		// Meeting: () => import('./components/Meeting'),
		Meeting,
		GlobalAlerts,
		DesktopAppUpdateAlert,
		DevTools,
		AlertModal,
		UserModal,
	},
	data() {
		return {
			store,
			omnibox: false,
			errorType: 'cookie',
			showError: false,
			showAuthSpinner: false,
			isWrapperManager: false,
			isAuthOnly: false,
			showLoadingScreen: true,
			alertModalProps: null,
			userModalUserId: null,
			userModalChatId: null,
		}
	},

	provide() {
		return {
			// used by PhotoVideo to autoplay gif images
			getInitalMark: () => null,
			maxMediaSize: IMAGE_MAX_WIDTH,
		}
	},

	watch: {
		'store.auth.loggedin': {
			handler(val, oldVal) {
				if (val && !oldVal) {
					let data = { type: 'authenticated', host: window.location.host }
					window.parent.postMessage(data, '*')
				}
			},
			deep: true,
		},
	},

	created() {
		if (storage.test() === false) {
			this.showError = true
			return
		}

		this.showLoadingScreen = false

		this.$onEventBus({
			logoutUser: () => {
				this.logout()
			},
			logoutAndRedirect: () => {
				this.logoutAndRedirect()
			},
			toggleAlertModal: (props) => {
				this.alertModalProps = props || null
			},
			showUserModal: (data) => {
				this.userModalUserId = null
				this.userModalChatId = null
				if (data?.userId) {
					this.$nextTick(() => {
						this.userModalUserId = data.userId
						this.userModalChatId = data.chatId || null
					})
				}
			},
		})

		this.isAuthOnly = window.appConfig.authonly

		if (this.$route.path === '/wrapper/manager') {
			this.isWrapperManager = true
		}

		if (window.appConfig.hasOwnProperty('user')) {
			const { integration_name, user, wrapper } = window.appConfig
			const isBlackboard = integration_name === 'blackboard'
			const isCanvas = integration_name === 'canvas'
			const noTOS = !user.acceptedtos
			storage.set('acceptedtos', user.acceptedtos ? user.acceptedtos : false)
			if (noTOS && (isBlackboard || (isCanvas && !wrapper))) {
				this.store.termsPrompt = true
			}
		} else {
			const { integration_name, wrapper } = window.appConfig
			const isBlackboard = integration_name === 'blackboard'
			const isCanvas = integration_name === 'canvas'
			const acceptTOS = storage.get('acceptedtos')
			if (!acceptTOS && (wrapper || isBlackboard || (isCanvas && !wrapper))) {
				this.store.termsPrompt = true
			}
		}

		if (this.$route.path === '/external_launch') {
			this.store.external_launch = true

			let query = Object.assign({}, this.$route.query)
			if (query.integration) {
				this.store.integration_name = query.integration
			}
		}

		if (
			!this.isWrapperManager &&
			!this.store.external_launch &&
			!this.showInvite
		) {
			new Image().src = '/images/common/dot.svg'
			new Image().src = '/images/common/large-grey-close.svg'
		}

		if (this.store.external_launch) {
			new Image().src = '/images/pronto/pronto-launch-header.svg'
			new Image().src = '/images/pronto/pronto-devices.svg'
		}

		if (this.store.persist === null || this.store.persist === 'undefined') {
			this.store.persist = false
		}
		// check for this key to be passed from Canvas
		if (
			window.appConfig.userKey &&
			window.appConfig.userKey !== this.store.userKey
		) {
			this.store.userKey = window.appConfig.userKey
			let data = {
				type: 'setSessionKey',
				session_key: window.appConfig.userKey,
			}
			window.parent.postMessage(data, '*')
			//delete window.appConfig.userKey;
		}

		browser.checkBrowser()

		this.store.config = window.appConfig
		this.store.timezone = moment.tz.guess()

		if (this.store.config.api_token) {
			if (this.store.config.persist === 'true') {
				this.store.persist = true
			} else if (this.store.config.persist === 'false') {
				this.store.persist = false
			}

			if (this.store.config.userKey) {
				this.store.auth.set(
					this.store.config.api_token,
					null,
					this.store.config.user,
					this.store.config.userKey,
					this.store.persist,
					this.store.config.device_uuid
				)

				if (store.config.device_type) {
					storage.set('device_type', store.config.device_type)
				}

				this.store.translations.init()
			} else {
				this.logout()
			}
		}

		this.store.auth.initialize(this.store.userKey)
		this.store.seedDB()
		window.onfocus = this.focus
		window.onblur = this.blur
		this.focus()

		// // omnibox handler
		listenForKeyboardShortcut('Cmd+K', () => {
			const { path = '' } = this.$route || {}
			if (path.startsWith('/meeting')) return
			this.accessibilityMode = false
			this.launchOmnibox()
		})

		document.addEventListener('keyup', ({ target, key }) => {
			if (
				this.store.config.embedded ||
				this.isWrapperManager ||
				this.store.config.wrapper ||
				this.store.external_launch
			) {
				return
			}
			if (key === 'Tab') {
				const borderelm = document.querySelector(
					'.accessibility-highlight-border'
				)
				borderelm.style.display = 'none'
			}
			if (
				key === 'Tab' &&
				target.tagName !== 'BODY' &&
				!['textarea', 'text', 'email', 'search'].includes(target.type) &&
				!['no-highlight', 'input-tel__input'].some((c) =>
					target.classList.contains(c)
				)
			) {
				highlightFocus(target)
			}
		})

		document.addEventListener('click', (e) => {
			if (
				this.store.config.embedded ||
				this.isWrapperManager ||
				this.store.config.wrapper ||
				this.store.external_launch
			) {
				return
			}
			var borderelm = document.querySelector('.accessibility-highlight-border')
			borderelm.style.display = 'none'
		})

		window.addEventListener('message', (e) => {
			var data = e.data
			switch (data.type) {
				case 'authenticating':
					this.showAuthSpinner = true
					break

				case 'authError':
					this.showError = true
					break

				case 'inFocus':
					var external_user = Number(storage.get('external_user_id'))
					if (!external_user || external_user === 'null') {
						storage.set('external_user_id', data.login_id)
					} else {
						if (external_user !== data.login_id) {
							this.logout()
						}
					}
					break

				case 'wrapperTimeout':
					sessionStorage.removeItem('userKey')
					break

				case 'userIsActive':
					this.store.auth.user.hasactivity = true
					this.store.auth.setUser(this.store.auth.user, this.store.userKey)
					break

				case 'newInstanceUrl':
					if (!this.store.canvasInstanceUrl) {
						this.store.canvasInstanceUrl = data.instance_url
						this.store.canvasAcctId = data.canvas_accountid
					}
					break
			}
		})

		try {
			let query = Object.assign({}, this.$route.query)
			if (
				query.api_token ||
				query.session_key ||
				query.push_token ||
				query.logintoken
			) {
				delete query.api_token
				delete query.session_key
				delete query.logintoken
				delete query.devicetype
				delete query.from
				this.$router.replace({
					...this.$route,
					query,
				})
			}
		} catch (e) {
			console.error('An error occurred while replacing query params:', e)
		}

		if (
			this.store.config.integration_name !== 'canvas' &&
			!this.store.config.wrapper &&
			this.store.auth.user
		) {
			this.store.auth.user.hasactivity = true
			this.store.auth.setUser(this.store.auth.user, this.store.userKey)
		}
	},

	mounted() {
		const { auth, config } = this.store

		if (!auth.loggedin && !browser.isMS) {
			try {
				this.store.dbClearAllTables()
			} catch (error) {}
		}

		if (config.wrapper && !auth.loggedin && !browser.isMS) {
			let data = { type: 'notAuthenticated' }
			window.parent.postMessage(data, '*')
		}
	},

	methods: {
		focus() {
			const dormancyMs = this.store.dormantSince
				? Date.now() - this.store.dormantSince
				: 0
			const pusher = getPusherInstance()
			const pusherState = pusher?.connection?.state
			console.debug(
				`[dormancy] focus elapsed=${Math.round(
					dormancyMs / 1000
				)}s pusherState=${pusherState}`
			)

			// After extended dormancy the OS may have killed the WebSocket
			// while Pusher's state machine still reports 'connected'. Verify
			// with a ping/pong before trusting the connection.
			//
			// pusher.connection is the ConnectionManager, which only forwards
			// message/ping/activity/error/closed from the inner Connection —
			// it does NOT propagate the 'pong' event. So we listen on
			// 'message' and filter for pusher:pong instead.
			const DORMANCY_THRESHOLD_MS = 60 * 60 * 1000
			if (
				dormancyMs > DORMANCY_THRESHOLD_MS &&
				pusher?.connection?.state === 'connected' &&
				!this._dormancyPingInFlight
			) {
				console.debug('[dormancy] >1hr dormant, sending pusher:ping')
				this._dormancyPingInFlight = true
				let pongReceived = false
				const messageHandler = (message) => {
					if (message.event === 'pusher:pong') {
						pongReceived = true
						pusher.connection.unbind('message', messageHandler)
						clearTimeout(this._pongTimeoutId)
						this._pongTimeoutId = null
						this._dormancyPingInFlight = false
						console.debug('[dormancy] pong received, emitting dormancy-refresh')
						EventBus.$emit('dormancy-refresh')
					}
				}
				pusher.connection.bind('message', messageHandler)

				const sent = pusher.send_event('pusher:ping', {})
				if (!sent) {
					console.warn('[dormancy] pusher:ping send failed, forcing reconnect')
					pusher.connection.unbind('message', messageHandler)
					this._pongTimeoutId = null
					this._dormancyPingInFlight = false
					pusher.disconnect()
					pusher.connect()
				} else {
					this._pongTimeoutId = setTimeout(() => {
						this._pongTimeoutId = null
						if (!pongReceived) {
							pusher.connection.unbind('message', messageHandler)
							this._dormancyPingInFlight = false
							if (pusher.connection.state === 'connected') {
								console.warn('[dormancy] no pong after 10s, forcing reconnect')
								pusher.disconnect()
								pusher.connect()
							}
						}
					}, 10000)
				}
			}

			this.store.dormantSince = null
			this.store.appVisible = true
			store.setActiveUserCookie()
			EventBus.$emit('app-visible', true)
		},

		blur() {
			this.store.dormantSince = Date.now()
			console.debug('[dormancy] blur')
			this.store.appVisible = false
			EventBus.$emit('app-visible', false)
			if (
				this.store.config.embedded ||
				this.isWrapperManager ||
				this.store.config.wrapper ||
				this.store.external_launch
			) {
				return
			}
			var borderelm = document.querySelector('.accessibility-highlight-border')
			borderelm.style.display = 'none'
		},

		launchOmnibox() {
			this.omnibox = true
		},

		closeOmnibox() {
			this.omnibox = false
		},

		async logout() {
			await logoutUser()
			window.location = getLoginUrl()
		},

		async logoutAndRedirect() {
			const org = getOrganization().name || ''
			await logoutUser()
			this.$router.push(`/login?loggedout=1&org=${org}`)
		},
	},

	computed: {
		hasActivity() {
			return this.store.auth.user.hasactivity
		},

		isMS() {
			return browser.isMS
		},

		isDesktopApp() {
			return !!window.todesktop
		},

		showLogin() {
			const { hostname } = window.location
			const { path } = this.$route || {}
			const { auth, config } = this.store
			return (
				!this.showLoadingScreen &&
				!this.showInvite &&
				(!auth.loggedin ||
					hostname.startsWith('accounts.') ||
					path === '/login') &&
				!browser.isMS &&
				!config.embedded &&
				!config.wrapper &&
				!this.showError &&
				!this.isWrapperManager
			)
		},

		showOAuth() {
			const { path = '' } = this.$route || {}
			return path.startsWith('/oauth/authorize')
		},

		showHome() {
			return (
				!this.showLoadingScreen &&
				store.auth.loggedin &&
				!browser.isMS &&
				!this.showError &&
				!this.isWrapperManager &&
				!store.termsPrompt &&
				!this.showMeeting &&
				this.hasActivity
			)
		},

		showMeeting() {
			const { path = '' } = this.$route || {}
			return !this.showLoadingScreen && path.startsWith('/meeting')
		},

		showInvite() {
			const { path = '' } = this.$route || {}
			return path.startsWith('/invite')
		},

		showDevTools() {
			return storage.get('pronto_dev_tools') || false
		},

		showOrgManagement() {
			const { path = '' } = this.$route || {}
			return path.startsWith('/organization')
		},

		// Standalone activity iframe route — render the router-view directly
		// without Home chrome (chat list, sidebar) or OrgManagement chrome.
		showActivityIframe() {
			const { path = '' } = this.$route || {}
			return path.startsWith('/activity')
		},

		shouldRenderHome() {
			return (
				!this.showLoadingScreen &&
				!this.showInvite &&
				!this.showLogin &&
				!this.showOAuth &&
				this.showHome &&
				!this.isAuthOnly &&
				!this.showOrgManagement &&
				!this.showActivityIframe &&
				!this.showMeeting &&
				!this.showError
			)
		},

		shouldRenderRouterView() {
			return (
				!this.showLoadingScreen &&
				!this.showLogin &&
				!this.showOAuth &&
				!this.showMeeting &&
				!this.shouldRenderHome &&
				!this.isWrapperManager &&
				!this.showAcceptTerms
			)
		},

		showAcceptTerms() {
			return (
				(this.store.termsPrompt &&
					this.store.config.wrapper &&
					!this.isWrapperManager) ||
				(this.store.termsPrompt &&
					this.store.config.integration_name === 'blackboard') ||
				(this.store.termsPrompt &&
					this.store.config.integration_name === 'canvas' &&
					!this.store.config.wrapper)
			)
		},
	},
}
</script>
